import type { ExperienceVariable, RichText } from '@matter_extractor/shared';
import {
  escapeHtml,
  experienceSegmentsOf,
  experienceVariableToken,
  renderRichText,
} from '@matter_extractor/shared';

/**
 * The texts of a page, their variables resolved - or NAMED, for a preview
 * (§8.6; grill Variables of 2026-09-16, D4, D5).
 *
 * Two readers of one token, and they must never be the same function:
 *
 *   - the SESSION resolves. `readerPlainText` keeps the unresolved spelling
 *     exactly as `renderRichText` keeps it - §8.6 refuses an unresolved variable
 *     at the version save, and blanking it here would hide the very defect that
 *     refusal exists for. The session's own resolver is total anyway
 *     (`resolvePersonVariable`), so nothing raw reaches the person (H62);
 *   - a PREVIEW names. « L'aperçu d'une page la peint dans les couleurs du
 *     thème et montre le nom, jamais une valeur inventée » (D5): a value would
 *     read as data somebody typed. So the two functions below draw a pill in the
 *     theme's accent carrying the variable's label.
 *
 * ## Un aperçu peut résoudre CERTAINS jetons, un par un (issue #141)
 *
 * The two preview functions take an OPTIONAL `resolveVariable` beside their
 * namer, and they ask it token by token: a value comes out as words, a `null`
 * comes out as the pill. Nothing of the session moves - it never traverses
 * these two functions, it goes through `readerPlainText` and through
 * `renderRichText` bare - and a preview that passes no resolver draws exactly
 * the pills it drew before.
 *
 * It exists for one surface, the full-screen preview: a field FILLED IN the
 * preview is a value that was typed rather than invented, so D5's refusal does
 * not reach it, while an objective, a criterion and an empty field keep the
 * pill. The pill is what says « ceci n'est pas du contenu »; rendered as plain
 * words, « @prénom » reads as a sentence of the page.
 *
 * ## The preview pill is painted through the renderer, not around it
 *
 * `renderRichText` is the pure function of `shared` the reader and the editor
 * both paint with (§9.2), and it stays untouched: it escapes every character it
 * emits, so no markup can come out of it. To draw a pill anyway, each variable
 * is resolved to a SENTINEL - two private-use code points around an index - that
 * the renderer escapes as itself, and the sentinel is replaced by the pill
 * AFTER, in the string the renderer produced. Nothing typed by an operator can
 * contain a private-use character that this file would then mistake for a
 * sentinel, because the sentinel is minted here and never stored.
 */

/**
 * A PLAIN text with its variables resolved - the Checkbox label, and nothing
 * else.
 *
 * §9.2 grants variables to the rich text of a Texte block and to a Checkbox
 * label (« libellé, variables acceptées »), and `experienceTextSitesOf` says so
 * in as many words. A Bouton's label, an Input's label and an image caption are
 * NOT among them, so the reader renders those verbatim: a zone that accepted a
 * token nobody resolves would be a trap rather than a feature.
 *
 * Returns a STRING and never HTML: a checkbox label has no marks, so there is
 * nothing here for `dangerouslySetInnerHTML` to do and React escapes it on the
 * way out.
 */
export function readerPlainText(
  text: string,
  resolveVariable?: (variable: ExperienceVariable) => string | null,
): string {
  if (resolveVariable === undefined) return text;
  return experienceSegmentsOf(text)
    .map((segment) =>
      segment.kind === 'text'
        ? segment.text
        : (resolveVariable(segment.variable) ?? experienceVariableToken(segment.variable)),
    )
    .join('');
}

/** One piece of a plain text as a preview draws it: words, or a named pill. */
export type ReaderPreviewSegment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'variable'; readonly label: string };

/**
 * A Checkbox label for a preview: its tokens named, for the component to draw
 * as pills - or RESOLVED, one by one, when a resolver answers (see the head).
 *
 * A resolved token becomes a `text` segment and not a named one, deliberately:
 * the component draws a pill around a `variable` segment, and a value wearing
 * the pill would say « ceci n'est pas du contenu » about the one thing here
 * that is.
 */
export function readerPreviewSegments(
  text: string,
  variableNames: (variable: ExperienceVariable) => string,
  resolveVariable?: (variable: ExperienceVariable) => string | null,
): ReaderPreviewSegment[] {
  return experienceSegmentsOf(text).map((segment) => {
    if (segment.kind === 'text') return { kind: 'text', text: segment.text };
    const value = resolveVariable?.(segment.variable) ?? null;
    if (value !== null) return { kind: 'text', text: value };
    return { kind: 'variable', label: variableNames(segment.variable) };
  });
}

const SENTINEL_OPEN = '';
const SENTINEL_CLOSE = '';
const SENTINEL = /(\d+)/g;

/** The class `reader.css` paints the preview pill with, and its mark. */
export const READER_VARIABLE_CLASS = 'lecteur-variable';

/**
 * The two sentinel characters, as a class - what a RESOLVED value is stripped
 * of before it joins the string (issue #141).
 *
 * The head's promise held as long as every character of that string came from
 * the stored document: an operator cannot type a private-use character into the
 * editor's WYSIWYG. A value typed INTO THE PREVIEW is the first string here
 * that never passed through the editor, so it is the first that could carry one
 * pasted from somewhere. Stripping is enough - the substitution only ever emits
 * an escaped label inside a fixed span, so the worst a forged sentinel could do
 * is draw a pill nobody asked for - and it keeps the promise literal rather
 * than nearly true.
 */
const SENTINEL_CHARS = new RegExp(`[${SENTINEL_OPEN}${SENTINEL_CLOSE}]`, 'g');

/**
 * A Texte block for a preview: the SAME markup the session renders, every
 * token replaced by a pill carrying its name - see the head.
 *
 * With a `resolveVariable`, a token the resolver answers comes out as its VALUE
 * instead, escaped by `renderRichText` like any other run of words. The choice
 * is made inside the resolver handed to the renderer, so there is still ONE
 * pass over the document and still no second renderer.
 */
export function readerPreviewHtml(
  document: RichText,
  variableNames: (variable: ExperienceVariable) => string,
  resolveVariable?: (variable: ExperienceVariable) => string | null,
): string {
  const labels: string[] = [];
  const html = renderRichText(document, {
    resolveVariable: (variable) => {
      const value = resolveVariable?.(variable) ?? null;
      if (value !== null) return value.replace(SENTINEL_CHARS, '');
      labels.push(variableNames(variable));
      return `${SENTINEL_OPEN}${labels.length - 1}${SENTINEL_CLOSE}`;
    },
  });
  return html.replace(
    SENTINEL,
    (_, index: string) =>
      `<span class="${READER_VARIABLE_CLASS}" data-testid="lecteur-variable">${escapeHtml(labels[Number(index)] ?? '')}</span>`,
  );
}
